import { AUDIT_CATEGORIES, AUDIT_DEFAULT_WINDOW_DAYS, type AuditCategory } from '@nest-recruit/shared';
import { dhakaDayEndIso, dhakaDayStartIso } from '../../../../lib/dhaka-time';
import { formatIsoDate } from '../../../../lib/format';

// USER-06's address (06-qol §3 and §6): the kind, Who, When, the search and the entry open on the right, so a copied
// link shows a colleague the same feed and the same entry. The names are the API's own (`?category=…&actorId=…`).

/** The quick "When" choices. 30 days is the default and is never written into the address. */
export const AUDIT_WINDOW_DAYS = [7, AUDIT_DEFAULT_WINDOW_DAYS, 90] as const;
export type AuditWindowDays = (typeof AUDIT_WINDOW_DAYS)[number];

export interface AccessLogSearch {
  readonly category?: AuditCategory;
  /** "Who": everything this person did. */
  readonly actorId?: string;
  readonly q?: string;
  /** "Last N days", when it is not the default 30. */
  readonly days?: Exclude<AuditWindowDays, typeof AUDIT_DEFAULT_WINDOW_DAYS>;
  /** A chosen range, as Dhaka calendar days (`YYYY-MM-DD`). Either end may be open. Wins over `days`. */
  readonly from?: string;
  readonly to?: string;
  /** The entry open on the right (on a phone, as its own view). */
  readonly entry?: string;
}

/** The filters the server reads, with "When" turned into instants. Absent `from` means the server's last 30 days. */
export interface AuditFilters {
  readonly category?: AuditCategory;
  readonly actorId?: string;
  readonly q?: string;
  readonly from?: string;
  readonly to?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DAY = /^\d{4}-\d{2}-\d{2}$/;

/** Text from the address: TanStack's reader turns `?q=2024` into a number, so a number is read back as text. */
function text(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** The page's reading of the address. Anything unknown or malformed is dropped, never an error. */
export function readAccessLogSearch(raw: Readonly<Record<string, unknown>>): AccessLogSearch {
  const next: { -readonly [K in keyof AccessLogSearch]: AccessLogSearch[K] } = {};
  const category = text(raw.category);
  if (category && (AUDIT_CATEGORIES as readonly string[]).includes(category)) next.category = category as AuditCategory;
  const actorId = text(raw.actorId);
  if (actorId && UUID.test(actorId)) next.actorId = actorId;
  const q = text(raw.q);
  if (q !== undefined) next.q = q.slice(0, 120);
  const from = text(raw.from);
  if (from && DAY.test(from) && dhakaDayStartIso(from)) next.from = from;
  const to = text(raw.to);
  if (to && DAY.test(to) && dhakaDayEndIso(to)) next.to = to;
  const days = Number(raw.days);
  if (next.from === undefined && next.to === undefined && (days === 7 || days === 90)) next.days = days;
  const entry = text(raw.entry);
  if (entry && UUID.test(entry)) next.entry = entry;
  return next;
}

/** A filter change: the new filters, keeping the open entry. A range replaces the quick window and the reverse. */
export function withFilters(search: AccessLogSearch, patch: Partial<Omit<AccessLogSearch, 'entry'>>): AccessLogSearch {
  const next: Record<string, unknown> = { ...search, ...patch };
  if ('days' in patch) {
    delete next.from;
    delete next.to;
  }
  if ('from' in patch || 'to' in patch) delete next.days;
  for (const key of Object.keys(next)) if (next[key] === undefined || next[key] === '') delete next[key];
  return next as AccessLogSearch;
}

/**
 * What the feed and the export ask the server for, "When" resolved at `now`. Review R7: "Until" alone is the 30 days before that
 * day, never the last 30 days from today (which would show nothing before it), so the window is sent whole, as the server would take it.
 */
export function filtersOf(search: AccessLogSearch, now: Date = new Date()): AuditFilters {
  const to = search.to ? dhakaDayEndIso(search.to) : null;
  const from = search.from
    ? dhakaDayStartIso(search.from)
    : search.days
      ? new Date(now.getTime() - search.days * DAY_MS).toISOString()
      : to
        ? new Date(Date.parse(to) - AUDIT_DEFAULT_WINDOW_DAYS * DAY_MS).toISOString()
        : null;
  return {
    ...(search.category ? { category: search.category } : {}),
    ...(search.actorId ? { actorId: search.actorId } : {}),
    ...(search.q ? { q: search.q } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };
}

/** The kinds' counts are for the same Who, When and search, whatever kind is picked. */
export function summarySearchOf(search: AccessLogSearch): AccessLogSearch {
  const { category: _category, entry: _entry, ...rest } = search;
  return rest;
}

/** The feed's own query key part: the filters without the open entry, so opening one never refetches. */
export function feedSearchOf(search: AccessLogSearch): AccessLogSearch {
  const { entry: _entry, ...rest } = search;
  return rest;
}

/** How many filters narrow the feed, for "Clear filters". */
export function filterCount(search: AccessLogSearch): number {
  return [search.category, search.actorId, search.q, search.days ?? search.from ?? search.to].filter((value) => value !== undefined).length;
}

/** "Last 30 days", "Last 7 days", "12-10-2026 to 20-10-2026", "From 12-10-2026", "30 days until 20-10-2026". */
export function whenLabel(search: AccessLogSearch): string {
  if (search.from && search.to) return search.from === search.to ? formatIsoDate(search.from) : `${formatIsoDate(search.from)} to ${formatIsoDate(search.to)}`;
  if (search.from) return `From ${formatIsoDate(search.from)}`;
  // Review R7: "Until" alone covers the 30 days before it, and says so.
  if (search.to) return `${AUDIT_DEFAULT_WINDOW_DAYS} days until ${formatIsoDate(search.to)}`;
  return `Last ${search.days ?? AUDIT_DEFAULT_WINDOW_DAYS} days`;
}
